import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PawPrint } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        // Forward any plan selected before Google sign-in so the server can gate a paid
        // signup to /checkout (server-authoritative; plan only affects brand-new tenants).
        const plan = sessionStorage.getItem("pawkt_signup_plan") || undefined;
        const { data } = await api.post("/auth/session", { session_id: sid, plan });
        sessionStorage.removeItem("pawkt_signup_plan");
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        const dest = data.user?.signup_payment_required
          ? "/checkout"
          : (data.user?.tenant?.onboarded ? "/dashboard" : "/onboarding");
        navigate(dest, { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail;
        const base = "Google sign-in didn't complete. Please try again or sign in with email.";
        sessionStorage.setItem("pawkt_login_error", typeof msg === "string" && msg && e?.response?.status === 403 ? msg : base);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <div className="h-12 w-12 rounded-2xl bg-slate-900 flex items-center justify-center">
        <PawPrint className="h-6 w-6 text-emerald-400" />
      </div>
      <div className="flex items-center gap-2 text-slate-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
        Signing you in…
      </div>
    </div>
  );
}
